import { Component, ViewChild, inject } from "@angular/core";
import { restManager } from "app/ajs-upgraded-providers";
import { ToolbarEventsService } from "src/app/core";
import { GridDownloadDialogService } from "src/app/dialogs";
import { GridCellFormatterService, GridClientSideComponent, GridFixedButtons, headersTranslate } from "src/app/shared/components";
import { Subscription, catchError, from, map, of } from "rxjs";
import { DataExport, FileExporterFe, TableData } from "src/app/shared/model/model";
import { TilbyDatePipe } from "@tilby/tilby-ui-lib/pipes/tilby-date";
import { autobind } from "src/app/models/decorators.model";
import { GridClickableButtonFileExporterComponent } from "src/app/shared/components/grid/grid-clickable-button-file-exporter/grid-clickable-button-file-exporter.component";
import { Rest } from "src/app/models";
import { subscribeInComponent } from "src/app/shared/angular-utils";

@Component({
	standalone: false,
    selector: 'app-settings-file-exporter-showcase',
    templateUrl: './settings-file-exporter-showcase.component.html',
    styleUrls: ['./settings-file-exporter-showcase.component.scss'],
})
export class SettingsFileExporterShowcaseComponent {
    @ViewChild("appGridComponent", { static: true }) gridRef!: GridClientSideComponent;
    private readonly gridCellFormatterService = inject(GridCellFormatterService);
    private readonly toolbarEventsService = inject(ToolbarEventsService);
    private readonly restManagerService = inject(restManager);
    private readonly gridDownloadDialogService = inject(GridDownloadDialogService);

    private movColumnsSwitch = false;
    private isAdvancedFilterActive = false;
    selectRowUIEnabled: boolean = true;
    fileIntervalSubscription: Subscription = new Subscription();
    checkFileStatusSubscription: Subscription = new Subscription();

    fixedButtons: GridFixedButtons = {
        checkbox: { visible: false, lockPosition: "left" },
        customButton: {
            visible: true,
            cellRenderer: GridClickableButtonFileExporterComponent,
            lockPosition: "right",
        },
    };
    gridOptions = {
        loadingCellRendererParams: {
            loadingMessage: 'One moment please...',
        },
        enableCellChangeFlash: false
    };
    rows: FileExporterFe[] = [];
    tableData: TableData[] = [
        {
            rowData$: from<Promise<FileExporterFe[]>>(this.restManagerService.getList('massive_export/files'))
                .pipe(
                    map(res => {
                        this.gridRef.gridApi?.setGridOption('animateRows', false);
                        this.rows = res;
                        return res.sort((fileA, fileB) => (TilbyDatePipe.toUtcDateMillis(fileB.created_at) - TilbyDatePipe.toUtcDateMillis(fileA.created_at)))
                    }),
                    catchError((error) => {
                        return of([]);
                    })
                ),
            dataType: new FileExporterFe(),
            headersTranslate: headersTranslate.file_exporter,
            columnsFormatter: this.gridCellFormatterService.file_exported,
        },
    ];

    constructor() {
        subscribeInComponent(this.toolbarEventsService.events, event => {
            switch(event.type) {
                case 'toolbar.search':
                    return this.gridRef.onFilterTextBoxChanged(event.search);
                case 'grid.open_tool_panel':
                    return this.gridRef.openToolPanel(event.panel);
            }
        });
    }

    ngOnInit(): void {
        this.createToolbarButtons();
    }

    ngAfterViewInit(): void {
        this.gridRef.gridApi?.setGridOption('animateRows', false);
    }

    createToolbarButtons(){
        this.toolbarEventsService.moduleTitle.next("FILE_EXPORTER");
        this.toolbarEventsService.searchBarValue$.next('');
        this.toolbarEventsService.searchBarAction$.next({ type: 'grid.open_tool_panel', panel: 'filters' });
        this.toolbarEventsService.searchBarActionFilled$.next({ type: 'grid.open_tool_panel', panel: 'filters' });
        this.toolbarEventsService.buttons$.next({
            barButtons: [],
            panelButtons: [
                {
                isIt: () => true,
                name: 'columns_panel',
                icon: () => 'view_week',
                label: () => 'TOPBAR.ACTIONS.COLUMN_SELECTED',
                click: () => this.gridRef.openToolPanel('columns')
            },
            {
                isIt: () => true,
                name: 'movable_columns',
                icon: () => 'drag_indicator',
                label: () => this.movColumnsSwitch ? 'TOPBAR.ACTIONS.DISABLED_COLUMN_MOVEMENT': 'TOPBAR.ACTIONS.ENABLED_COLUMN_MOVEMENT',
                click: () => this.gridRef.columnMovableSwitch(this.movColumnsSwitch = !this.movColumnsSwitch)
            },
            {
                isIt: () => true,
                name: 'save_columns_position',
                icon: () => 'save',
                label: () => 'TOPBAR.ACTIONS.SAVE_COLUMN_PREFERENCE',
                click: () => this.gridRef.openDialogSaveColumnsPreference()
            },
            {
                isIt: () => true,
                name: 'advanced_filter',
                icon: () => this.isAdvancedFilterActive ? 'filter_alt_off' : 'filter_list_alt',
                label: () => this.isAdvancedFilterActive ? 'TOPBAR.ACTIONS.ADVANCED_FILTERS_DISABLED' : 'TOPBAR.ACTIONS.ADVANCED_FILTERS_ENABLED',
                click: () => this.gridRef.advancedFilterSwitch(this.isAdvancedFilterActive = !this.isAdvancedFilterActive)
            },
            {
                isIt: () => false,
                name: 'export',
                icon: () => 'download',
                label: () => 'TOPBAR.ACTIONS.EXPORT',
                click: async () => await this.gridDownloadDialogService.openDialog('file_exporter',{data: this.tableData.map(tableData => ({...tableData,sortFilterQuery: this.gridRef.getSortFilterQuery(),countSelectedRows:this.gridRef.gridApi?.getSelectedRows().length}))},'files').then(res=>!!res&&this.onExport(res,'files'))
            },
            ]
        })
    }

    crud({method, id}: Rest) {
        if (method === 'delete') this.gridRef.onRemoveSelected(id);
    }

    @autobind
    onExport(dataExport: DataExport,tableToCall?:string) {
        this.gridRef?.onBtnExport(dataExport,tableToCall);
    }
}